import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import DatePicker from "react-multi-date-picker";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import { Dropdown, Image } from "hesus-ui-kit";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  GridRow,
  GridCell,
  ErrorMessagePara,
} from "../../AddEditInvoice.styled";
import "../../AddEditInvoice.scss";

const FinancialInformation = ({
  t,
  formData,
  onFormChange,
  generalParams,
  vatRateList,
  setVatRateList,
  paymentTermsList,
  setPaymentTermsList,
}) => {
  const [bankAccountList, setBankAccountList] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [vatRateLoading, setVatRateLoading] = useState(true);
  const [paymentTermsLoading, setPaymentTermsLoading] = useState(true);
  const [bankAccountLoading, setBankAccountLoading] = useState(true);

  const fetchGeneralVatRate = () => {
    setVatRateLoading(true);
    axios
      .get(
        `/general/vatRates?countries=[${formData.countryId.value}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const vatData = response.data.vatRate.map((vats) => {
          return {
            label: vats.label,
            value: vats.id,
            calculationRate: vats.calculationRate,
          };
        });
        setVatRateList(vatData);
      })
      .catch(() => {
        setVatRateList([]);
      })
      .finally(() => {
        setVatRateLoading(false);
      });
  };

  const fetchPaymentTerms = () => {
    setPaymentTermsLoading(true);
    axios
      .get(
        `/general/paymentTerms?countries=[${formData.countryId.value}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const termData = response.data.paymentTerm.map((terms) => {
          return {
            label: terms.label,
            value: terms.id,
            calculationCode: terms.calculationCode,
            calculationValue: terms.calculationValue,
          };
        });
        setPaymentTermsList(termData);
      })
      .catch(() => {
        setPaymentTermsList([]);
      })
      .finally(() => {
        setPaymentTermsLoading(false);
      });
  };

  const fetchBankAccounts = () => {
    setBankAccountLoading(true);
    axios
      .get(
        `/general/bankAccounts?countries=[${formData.countryId.value}]&status=1&orderBy="asc"&orderColumn="id"&limit=-1`
      )
      .then((response) => {
        const termData = response.data.bankAccount.map((terms) => {
          return {
            label: terms.label,
            value: terms.id,
          };
        });
        setBankAccountList(termData);
      })
      .catch(() => {
        setBankAccountList([]);
      })
      .finally(() => {
        setBankAccountLoading(false);
      });
  };

  const fetchMultiCurrency = () => {
    const userCurrencyObj =
      generalParams?.userCurrencyList?.[formData.countryId.value];
    userCurrencyObj.reduce(
      (prev, current) => ({ ...prev, [current.id]: current.label }),
      {}
    );
    const currenciesList = userCurrencyObj.map((entry) => {
      return { label: entry["label"], value: entry["id"] };
    });
    setCurrencies(currenciesList);
  };

  useEffect(() => {
    fetchGeneralVatRate();
    fetchPaymentTerms();
    fetchBankAccounts();
    fetchMultiCurrency();
  }, [formData.countryId.value]);

  const getColumnForGrid = () => {
    let columns = "1fr 1fr 1fr 1fr 1fr";
    if (currencies?.length > 1) {
      columns = "10rem 1fr 1fr 1fr 1fr";
    }
    return columns;
  };

  useEffect(() => {
    if (vatRateLoading) {
      return;
    }
    const selectedVatRateId = formData.vatRateId.value;
    if (!selectedVatRateId) {
      return;
    }
    const exists = vatRateList.some((item) => item.value === selectedVatRateId);
    if (!exists) {
      onFormChange("vatRateId", "");
    }
  }, [vatRateList, vatRateLoading, formData.vatRateId.value]);

  useEffect(() => {
    if (paymentTermsLoading) {
      return;
    }
    const selectedPaymentTermId = formData.paymentTermId.value;
    if (!selectedPaymentTermId) {
      return;
    }
    const exists = paymentTermsList.some(
      (item) => item.value === selectedPaymentTermId
    );
    if (!exists) {
      onFormChange("paymentTermId", "");
    }
  }, [paymentTermsList, paymentTermsLoading, formData.paymentTermId.value]);

  useEffect(() => {
    if (bankAccountLoading) {
      return;
    }
    const selectedBankAccountId = formData.bankAccountId.value;
    if (!selectedBankAccountId) {
      return;
    }
    const exists = bankAccountList.some(
      (item) => String(item.value) === String(selectedBankAccountId)
    );
    if (!exists) {
      onFormChange("bankAccountId", "");
    }
  }, [bankAccountList, bankAccountLoading, formData.bankAccountId.value]);

  const isDocumentType1Required =
    Number(formData.documentType.value) === 1 &&
    [2, 10].includes(Number(formData.statusId.value));

  const isCurrencyRequired =
    isDocumentType1Required ||
    (Number(formData.documentType.value) === 2 &&
      Number(formData.statusId.value) === 2);

  const financeSectionErrors = [
    isCurrencyRequired ? formData.currencyId?.error : null,
    isDocumentType1Required ? formData.bankAccountId?.error : null,
    isDocumentType1Required ? formData.paymentTermId?.error : null,
    isDocumentType1Required ? formData.dueDate?.error : null,
    isDocumentType1Required ? formData.vatRateId?.error : null,
  ].filter(Boolean);

  return (
    <>
      <GridRow background="#008395" borderRadius="5px 5px 0 0px">
        <GridCell fontWeight="700" fontSize="16px">
          {t("CLIENT_INVOICING_SECTION_FINANCIAL_INFO")}
        </GridCell>
      </GridRow>
      {financeSectionErrors.length > 0 && (
        <GridRow gap="10px" padding="12px 16px 0px">
          <ErrorMessagePara>
            {t("CLIENT_INVOICING_ERROR_GENERAL_MISSING_FIELDS")}
          </ErrorMessagePara>
        </GridRow>
      )}
      <GridRow
        column="1fr"
        boxShadow={
          financeSectionErrors.length > 0
            ? "-1px 1px 1px #e5e5e5, 1px 1px 1px #e5e5e5, 0px 1px 1px #e5e5e5"
            : "0px 1px 1px 1px #e5e5e5"
        }
        margin="0 0 12px 0"
      >
        <GridRow column={getColumnForGrid()} padding="0px">
          {currencies?.length > 1 && (
            <Dropdown
              id="invoice-currency"
              label={t("CLIENT_INVOICING_CURRENCY")}
              name="CLIENT_INVOICING_CURRENCY"
              value={formData.currencyId.value || ""}
              onChange={(option) => onFormChange("currencyId", option.value)}
              defaultOption={t("CLIENT_INVOICING_CURRENCY")}
              options={currencies}
              labelStyles={
                formData.currencyId.error !== ""
                  ? labelStylesError
                  : labelStyles
              }
              inputStyles={
                formData.currencyId.error !== ""
                  ? inputStylesError
                  : inputStyles
              }
              error=""
              required={isCurrencyRequired}
            />
          )}
          <Dropdown
            id="invoice-vatRateId"
            label={t("CLIENT_INVOICING_VAT_RATE")}
            name="nvoice-vatRateId"
            value={formData.vatRateId.value}
            onChange={(option) => onFormChange("vatRateId", option.value)}
            defaultOption={t("CLIENT_INVOICING_VAT_RATE")}
            options={vatRateList}
            labelStyles={
              formData.vatRateId.error !== "" ? labelStylesError : labelStyles
            }
            inputStyles={
              formData.vatRateId.error !== "" ? inputStylesError : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error=""
            required={isDocumentType1Required}
          />
          <div style={{ marginTop: "1.5rem" }}>
            <Image
              src={formData.isSubcontracting.value ? enableIcon : disableIcon}
              alt={t("CLIENT_INVOICING_IS_SUBCONTRACTING")}
              title={t("CLIENT_INVOICING_IS_SUBCONTRACTING")}
              styles={{ width: "auto" }}
              onClick={() =>
                onFormChange(
                  "isSubcontracting",
                  !formData.isSubcontracting.value
                )
              }
            />
            <label className="label add-edit-invoice__active-label">
              {t("CLIENT_INVOICING_IS_SUBCONTRACTING")}
            </label>
          </div>
        </GridRow>
        <GridRow column="1fr 1fr 1fr 1fr" padding="0px">
          <Dropdown
            id="invoice-bankAccountId"
            label={t("CLIENT_INVOICING_BANK_ACCOUNT")}
            name="invoice-bankAccountId"
            value={formData.bankAccountId.value}
            onChange={(option) => onFormChange("bankAccountId", option.value)}
            defaultOption={t("CLIENT_INVOICING_BANK_ACCOUNT")}
            options={bankAccountList}
            required={isDocumentType1Required}
            labelStyles={
              formData.bankAccountId.error !== ""
                ? labelStylesError
                : labelStyles
            }
            inputStyles={
              formData.bankAccountId.error !== ""
                ? inputStylesError
                : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error=""
          />
          <Dropdown
            id="invoice-paymentTermId"
            label={t("CLIENT_INVOICING_PAYMENT_TERMS")}
            name="invoice-paymentTermId"
            value={formData.paymentTermId.value}
            onChange={(option) => {
              const selectedPaymentTerm = paymentTermsList.find(
                (item) => String(item.value) === String(option.value)
              );
              onFormChange("paymentTermId", option.value, {
                paymentTerm: selectedPaymentTerm,
              });
            }}
            defaultOption={t("CLIENT_INVOICING_PAYMENT_TERMS")}
            options={paymentTermsList}
            required={isDocumentType1Required}
            labelStyles={
              formData.paymentTermId.error !== ""
                ? labelStylesError
                : labelStyles
            }
            inputStyles={
              formData.paymentTermId.error !== ""
                ? inputStylesError
                : inputStyles
            }
            containerStyles={{ marginBottom: "7px" }}
            error=""
          />
          {formData.documentType?.value === 1 && (
            <div
              className={`${
                formData.dueDate.error
                  ? "add-edit-invoice__date-container_start_error"
                  : "add-edit-invoice__date-container_start"
              } ${
                isDocumentType1Required
                  ? "add-edit-invoice__date-container_start_required"
                  : ""
              }`}
            >
              <label htmlFor="due-date">{t("CLIENT_INVOICING_DUE_DATE")}</label>
              <DatePicker
                inputClass={
                  formData.dueDate.error
                    ? "add-edit-invoice__date-input-error"
                    : "add-edit-invoice__date-input"
                }
                containerClassName="add-edit-invoice__datepicker-container"
                type="text"
                required={isDocumentType1Required}
                label={t("CLIENT_INVOICING_DUE_DATE")}
                name="add-edit-invoice-due-date"
                id="add-edit-invoice-due-date"
                calendarPosition="bottom-center"
                isFloat
                onChange={(e) =>
                  onFormChange(
                    "dueDate",
                    moment(e.toDate()).format("YYYY-MM-DD")
                  )
                }
                style={{
                  marginTop: "1px",
                  padding: "7px 5px 5px 10px",
                  cursor: "pointer",
                  border: `1px solid ${
                    formData.dueDate.error ? "red" : "rgb(204, 204, 204)"
                  }`,
                  color: "rgb(71, 71, 71)",
                  borderRadius: "4px",
                }}
                format={getDatePickerFormat()}
                locale={getCalendarLocale()}
                value={
                  getDateFormat(formData.dueDate.value, "YYYY-MM-DD") || ""
                }
                containerStyles={{ marginTop: "21px" }}
                labelStyles={
                  formData.dueDate.error !== "" ? labelStylesError : labelStyles
                }
                error=""
                placeholder={t("CLIENT_INVOICING_DUE_DATE")}
              />
            </div>
          )}
        </GridRow>
      </GridRow>
    </>
  );
};

FinancialInformation.propTypes = {
  t: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  updateRefreshStatus: PropTypes.func,
};

FinancialInformation.defaultProps = {
  isEditMode: false,
  id: null,
  updateRefreshStatus: null,
};

export default withTranslation()(FinancialInformation);
